import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import { ReactComponent as ArrowBackIosNewIcon } from './icons/arrow_back_ios_24dp_5F6368_FILL0_wght400_GRAD0_opsz24.svg';
import { ReactComponent as ArrowForwardIosIcon } from './icons/arrow_forward_ios_24dp_5F6368_FILL0_wght400_GRAD0_opsz24.svg';

// ---------------------------------------------------------------------------
// Detect embed mode: dentro un iframe (cross-origin safe) oppure ?embed=1
// ---------------------------------------------------------------------------
const isEmbedMode = (() => {
  try {
    const inIframe = window.self !== window.top;
    const inQuery  = new URLSearchParams(window.location.search).get('embed') === '1';
    return inIframe || inQuery;
  } catch {
    return true; // accesso cross-origin bloccato → siamo in iframe
  }
})();

// ---------------------------------------------------------------------------

const ProductMediaSidebar = ({ isOpen, toggleSidebar, productMedia }) => {
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);

  // Costruisci l'array dei media disponibili
  const mediaItems = [];
  if (productMedia) {
    if (productMedia.urlImage)   mediaItems.push({ type: 'image', content: productMedia.urlImage });
    if (productMedia.urlVideo)   mediaItems.push({ type: 'video', content: productMedia.urlVideo });
    if (productMedia.url3Dviewer) {
      // Doppia garanzia: il backend già converte, ma sanitizziamo anche qui
      const secureUrl = productMedia.url3Dviewer.replace(/^http:\/\//i, 'https://');
      mediaItems.push({ type: '3d', content: secureUrl });
    }
  }

  const handlePrev = () =>
    setCurrentMediaIndex((i) => (i === 0 ? mediaItems.length - 1 : i - 1));
  const handleNext = () =>
    setCurrentMediaIndex((i) => (i === mediaItems.length - 1 ? 0 : i + 1));

  // ── MODALITÀ NORMALE (browser standalone) ────────────────────────────────
  if (!isEmbedMode) {
    return (
      <>
        {/* Linguetta – visibile quando chiusa */}
        <Box
          onClick={toggleSidebar}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '-40px',
            transform: 'translateY(-50%)',
            zIndex: 100,
            display: isOpen ? 'none' : 'flex',
            alignItems: 'center',
            bgcolor: 'white',
            color: 'black',
            p: 1, pl: 0.5, pr: 0.5,
            borderTopLeftRadius: 4,
            borderBottomLeftRadius: 4,
            boxShadow: 2,
            cursor: 'pointer',
            transition: 'background-color 0.3s ease',
            '&:hover': { bgcolor: 'grey.400' },
            marginRight: '-10px',
          }}
        >
          <IconButton size="small" sx={{ color: 'inherit' }}>
            <ArrowBackIosNewIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Box>

        {/* Pulsante chiusura – visibile quando aperta */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '-385px',
            transform: 'translateY(-50%)',
            zIndex: 800,
            display: isOpen ? 'flex' : 'none',
            bgcolor: 'white',
            color: 'black',
            p: 1, pl: 0.5, pr: 0.5,
            boxShadow: 2,
            borderTopLeftRadius: 4,
            borderBottomLeftRadius: 4,
          }}
        >
          <IconButton color="inherit" onClick={toggleSidebar} size="small">
            <ArrowBackIosNewIcon sx={{ fontSize: '1rem', color: 'black' }} />
          </IconButton>
        </Box>

        {/* Pannello sidebar */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            right: '100%',
            transform: isOpen
              ? 'translateX(0) translateY(-50%)'
              : 'translateX(-100%) translateY(-50%)',
            width: '300px',
            bgcolor: 'grey.200',
            p: 3,
            boxShadow: 3,
            zIndex: 950,
            overflowY: 'auto',
            transition: 'transform 0.3s ease-in-out',
            display: isOpen ? 'block' : 'none',
          }}
        >
          <SidebarContent
            mediaItems={mediaItems}
            currentMediaIndex={currentMediaIndex}
            handlePrev={handlePrev}
            handleNext={handleNext}
          />
        </Box>
      </>
    );
  }

  // ── EMBED MODE (iframe) ───────────────────────────────────────────────────
  return (
    <>
      {/* Backdrop semitrasparente – solo quando la sidebar è aperta */}
      <Box
        onClick={toggleSidebar}
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(0,0,0,0.45)',
          backdropFilter: 'blur(2px)',
          zIndex: 9990,
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          transition: 'opacity 0.3s ease',
        }}
      />

      {/* Tab laterale – "Media" pill, visibile quando chiusa */}
      <Box
        onClick={toggleSidebar}
        sx={{
          position: 'fixed',
          top: '50%',
          left: 0,
          transform: 'translateY(-50%)',
          zIndex: 9999,
          display: isOpen ? 'none' : 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          bgcolor: 'primary.main',
          color: 'white',
          py: 1.5,
          px: 0.75,
          borderTopRightRadius: 10,
          borderBottomRightRadius: 10,
          boxShadow: '2px 0 12px rgba(0,0,0,0.25)',
          cursor: 'pointer',
          transition: 'background-color 0.2s ease, transform 0.2s ease',
          '&:hover': {
            bgcolor: 'primary.dark',
            transform: 'translateY(-50%) translateX(3px)',
          },
        }}
      >
        <PermMediaIcon sx={{ fontSize: '1.1rem' }} />
        <Typography
          sx={{
            fontSize: '0.6rem',
            fontWeight: 700,
            letterSpacing: '0.05em',
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            transform: 'rotate(180deg)',
            userSelect: 'none',
          }}
        >
          MEDIA
        </Typography>
      </Box>

      {/* Pannello sidebar – slide-in da sinistra sopra la chat */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 9995,
          transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.35s cubic-bezier(0.4,0,0.2,1)',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          boxShadow: 6,
        }}
      >
        {/* Header del pannello */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            bgcolor: 'primary.main',
            color: 'white',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PermMediaIcon sx={{ fontSize: '1.2rem' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: '0.03em' }}>
              Media del Prodotto
            </Typography>
          </Box>
          <IconButton
            onClick={toggleSidebar}
            size="small"
            sx={{
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Contenuto scrollabile */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          <SidebarContent
            mediaItems={mediaItems}
            currentMediaIndex={currentMediaIndex}
            handlePrev={handlePrev}
            handleNext={handleNext}
            embedMode
          />
        </Box>
      </Box>
    </>
  );
};

// ---------------------------------------------------------------------------
// Componente condiviso: contenuto del carosello media
// ---------------------------------------------------------------------------
const SidebarContent = ({ mediaItems, currentMediaIndex, handlePrev, handleNext, embedMode }) => (
  <Paper
    elevation={embedMode ? 0 : 1}
    sx={{
      p: 2,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      position: 'relative',
      bgcolor: embedMode ? 'transparent' : undefined,
    }}
  >
    {mediaItems.length > 0 ? (
      <>
        {mediaItems.length > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
            <IconButton size="small" onClick={handlePrev}>
              <ArrowBackIosNewIcon style={{ fontSize: '1rem' }} />
            </IconButton>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {currentMediaIndex + 1} / {mediaItems.length}
            </Typography>
            <IconButton size="small" onClick={handleNext}>
              <ArrowForwardIosIcon style={{ fontSize: '1rem' }} />
            </IconButton>
          </Box>
        )}

        {mediaItems[currentMediaIndex].type === 'image' && (
          <Box sx={{ textAlign: 'center' }}>
            <img
              src={mediaItems[currentMediaIndex].content}
              alt="Media prodotto"
              style={{ width: '100%', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,.15)' }}
            />
          </Box>
        )}

        {mediaItems[currentMediaIndex].type === 'video' && (
          <Box>
            <video width="100%" controls style={{ borderRadius: 8 }}>
              <source src={mediaItems[currentMediaIndex].content} type="video/mp4" />
              Il tuo browser non supporta il video.
            </video>
          </Box>
        )}

        {mediaItems[currentMediaIndex].type === '3d' && (
          <Box>
            <iframe
              src={mediaItems[currentMediaIndex].content}
              title="3D Viewer"
              style={{ width: '100%', height: embedMode ? '55vh' : '300px', border: 'none', borderRadius: 8 }}
            />
          </Box>
        )}
      </>
    ) : (
      <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
        Nessun media disponibile per questo prodotto.
      </Typography>
    )}
  </Paper>
);

export default ProductMediaSidebar;
